import {
  ChangeDetectionStrategy,
  Component,
  computed,
  EventEmitter,
  inject,
  Input,
  input,
  OnInit,
  Output,
  Signal,
} from '@angular/core';

import { IconLibrary, SvgLibraryIcon } from '@finnairoyj/fcom-ui-styles';

import { ButtonMode, ButtonTheme } from '@fcom/ui-components/components/buttons/button/enums';
import { IconButtonTheme } from '@fcom/ui-components/components/buttons/icon-button/enums';
import { IconPosition } from '@fcom/ui-components/components/icons/enums';
import { LoaderTheme } from '@fcom/ui-components/components/loader/enums';
import { TagTheme } from '@fcom/ui-components/components/tag/interfaces';
import { SubCategory } from '@fcom/dapi/api/models/sub-category';
import { GroupedServices, ModifiedServiceInfo } from '@fcom/dapi/interfaces/services.interface';
import { Category } from '@fcom/dapi/api/models/category';
import { MediaQueryService } from '@fcom/common/services/media-query/media-query.service';
import { ConfigService } from '@fcom/core/services/config/config.service';
import { FinnairPassengerServiceSelectionItem } from '@fcom/dapi/api/models/finnair-passenger-service-selection-item';

import { AllowedActionsMode } from '../../enums';
import { isNotIncludedService } from '../../../../utils/common-booking.utils';

const CABIN_WEIGHT_VARIANTS = ['CABIN_WEIGHT', 'CABIN_WEIGHT_COMBINED', 'CABIN_WEIGHT_COMBINED_OLD'];

@Component({
  selector: 'fin-grouped-services',
  templateUrl: 'grouped-services.component.html',
  styleUrls: ['../../../../assets/helper-classes.scss', 'grouped-services.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class GroupedServicesComponent implements OnInit {
  private mediaQueryService = inject(MediaQueryService);
  private configService = inject(ConfigService);

  readonly ButtonTheme = ButtonTheme;
  readonly IconButtonTheme = IconButtonTheme;
  readonly ButtonMode = ButtonMode;
  readonly Category = Category;
  readonly SubCategory = SubCategory;
  readonly LoaderTheme = LoaderTheme;
  readonly SvgLibraryIcon = SvgLibraryIcon;
  readonly TagTheme = TagTheme;
  readonly isNotIncludedService = isNotIncludedService;
  readonly IconPosition = IconPosition;
  readonly IconLibrary = IconLibrary;
  readonly AllowedActionsMode = AllowedActionsMode;

  @Input()
  category: Category;

  @Input()
  groupedServices: GroupedServices[];

  loading = input<boolean>(false);

  @Input()
  enableUpsellMMB = false;

  @Input()
  isCarrierConnect = false;

  @Input()
  isInfant: boolean;

  mode = input<AllowedActionsMode>(AllowedActionsMode.INFO);
  serviceModifyProgress = input<boolean>(false);

  @Output() selectProduct: EventEmitter<ModifiedServiceInfo> = new EventEmitter<ModifiedServiceInfo>();

  @Output() removeProduct: EventEmitter<ModifiedServiceInfo> = new EventEmitter<ModifiedServiceInfo>();

  @Output() continuePurchase: EventEmitter<boolean> = new EventEmitter<boolean>();

  expandOpen = false;

  filteredGroupedServices: GroupedServices[];

  cabinWeight: number;

  isMobile: Signal<boolean>;

  ngOnInit(): void {
    this.isMobile = this.mediaQueryService.isBreakpoint('mobile');

    this.filteredGroupedServices = this.groupedServices.map((groupedService) => {
      const services = this.enableUpsellMMB
        ? groupedService.services.filter((service) => ![SubCategory.CABIN_WEIGHT].includes(service.subCategory))
        : groupedService.services;

      const servicesWithDisplayNameUseMapped = services.map((s) => ({
        ...s,
        useDisplayName: s.variant?.startsWith('BDL_'),
      }));

      return {
        ...groupedService,
        services: servicesWithDisplayNameUseMapped,
      };
    });

    this.cabinWeight = this.category === Category.CABIN_BAGGAGE ? this.getCabinWeight(this.groupedServices) : 0;
  }

  private getCabinWeight(groupedServices: GroupedServices[]): number {
    return (
      groupedServices
        .flatMap((groupedService) => groupedService.services)
        .find((service) => CABIN_WEIGHT_VARIANTS.includes(service.variant))?.parameters?.baggageWeight ?? 0
    );
  }

  shouldShowRemoveButton = computed(() => {
    const isReviewMode = this.mode() === AllowedActionsMode.CART;
    const checkInUpsellEnabled = this.configService.cfg.enableCaobCheckinSales;
    const showInCheckInMode =
      this.mode() === AllowedActionsMode.CHECK_IN &&
      ((checkInUpsellEnabled && this.category === Category.CABIN_BAGGAGE) || this.category === Category.SEAT);
    if (this.loading()) {
      return false;
    }
    return isReviewMode || showInCheckInMode;
  });

  shouldShowAddButton = computed(() => {
    if (this.loading()) {
      return false;
    }
    const showInMMB = this.mode() === AllowedActionsMode.MMB;
    const showInCheckInMode =
      this.mode() === AllowedActionsMode.CHECK_IN && this.category === Category.CABIN_BAGGAGE && this.enableUpsellMMB;
    return showInMMB || showInCheckInMode;
  });

  shouldShowModifyButton = computed(() => {
    if (this.loading()) {
      return false;
    }

    return [AllowedActionsMode.MMB, AllowedActionsMode.CART].includes(this.mode());
  });

  shouldShowPayButton = computed(() => {
    if (this.loading()) {
      return false;
    }

    return [AllowedActionsMode.MMB].includes(this.mode());
  });

  isCartMode = computed(() => this.mode() === AllowedActionsMode.CART);

  onSelectProduct(service: FinnairPassengerServiceSelectionItem): void {
    this.selectProduct.emit({
      category: this.category,
      variant: service.variant,
      seatNumber: service.seatNumber,
    });
  }

  onRemoveProduct(service: FinnairPassengerServiceSelectionItem): void {
    this.removeProduct.emit({
      category: this.category,
      variant: service.variant,
      seatNumber: service.seatNumber,
    });
  }
}
